// Polyfills
import 'regenerator-runtime/runtime';

import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import scrollToComponent from 'react-scroll-to-component';
import { toast } from 'react-toastify';
import {v1 as uuidv1} from 'uuid';

import dotProp from 'dot-prop-immutable';

import ProductOverviewHeader from './ProductOverviewHeader';
import ProductOverviewResults from './ProductOverviewResults';
import ProductOverviewFilter from './ProductOverviewFilter';
import ProductOverviewPagination from './ProductOverviewPagination';
import BrandLogoBar from './BrandLogoBar';
import PortalReplace from "./PortalReplace";
import { StaticData } from '../../context/staticData';
import { getFormParams } from '../../helpers/getFormParams';

export default function ProductOverview({ updateUrl, placeholderUrl, customOptions }) {
    const urlParams = new URLSearchParams(location.search);

    if (!urlParams.has('sort')) {
        urlParams.set('sort', customOptions?.defaultSort || '');
    }

    if(customOptions?.brand) {
        urlParams.set('brand[]', customOptions?.brand);
    }

    const resultsRef = useRef(null);
    const [sorting, setSorting] = useState([]);
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState([]);
    const [newFilters, setNewFilters] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [query, setQuery] = useState(urlParams.has('query') ? urlParams.get('query') : null);
    const formRef = useRef();
    const [sessionId, setSessionId] = useState('');

    const reorderFiltersByBrand = (filters, brand) => {
        if (!brand || filters.length === 0) return filters;

        const brandFilterIndex = filters.findIndex(filter => filter.key === 'brand');
        if (brandFilterIndex === -1) return filters;

        const choices = filters[brandFilterIndex].filters[0].choices;
        const matchingBrandIndex = choices.findIndex(choice => choice.key === brand);
        if (matchingBrandIndex === -1) return filters;

        const reorderedChoices = [
            choices[matchingBrandIndex],
            ...choices.filter((_, i) => i !== matchingBrandIndex),
        ];

        return filters.map((filter, index) =>
            index === brandFilterIndex
                ? {...filter, filters: filter.filters.map((f, i) =>
                    i === 0 ? {...f, choices: reorderedChoices} : f
                  )}
                : filter
        );
    };

    useEffect(() => {
        if (sessionId === '') {
            const newSessionId = urlParams.has('uid') ? urlParams.get('uid') : uuidv1();
            setSessionId(newSessionId);
            urlParams.set('uid', newSessionId);
        }

        fetchData(urlParams.has('page') ? urlParams.get('page') : page, false, urlParams, true);
    }, []);

    useEffect(() => {
        if (newFilters.length > 0) {
            fetchData(1, true, new FormData(formRef.current));
        }
    }, [newFilters]);

    const fetchData = async (page, saveParams = true, formData) => {
        setItems([]);
        setIsLoading(true);

        formData.set('page', page);

        const params = getFormParams(formData);

        if (saveParams) replaceHistoryState(params);

        try {
            const result = await Axios.get(updateUrl, { params: params });

            if (result.data) {
                const { sorting, items, filters, count, pages } = result.data;

                setSorting(sorting.items);
                setItems(items);
                setFilters(reorderFiltersByBrand(filters.items, customOptions?.brand));
                setTotalResults(count);
                setPage(pages.current);
                setTotalPages(pages.total);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            toast.error('Er is iets verkeerd gegaan, probeer het nogmaals.');
            throw new Error(error);
        }
    };

    const replaceHistoryState = (params) => {
        history.replaceState({}, document.title, `${location.pathname}${[...params].length > 0 ? `?${params}` : ''}`);
    };

    const updateFilters = (type, values) => {
        if (type === 'filters') setFilters(values);

        // TODO: Fix this
        setTimeout(() => {
            if (type === 'filters') setNewFilters(values);
        }, 100);

        if (type === 'sorting') {
            setSorting(values);
            setTimeout(() => {
                fetchData(1, true, new FormData(formRef.current));
            }, 100);
        }
    };

    const updateQuery = (value) => {
        setQuery(value);
        fetchData(page, true, new FormData(formRef.current));
    };

    const updateCurrentPage = (data) => {
        const page = data.selected;
        fetchData(page + 1, true, new FormData(formRef.current));

        scrollToComponent(resultsRef.current, {
            offset: -200,
            align: 'top',
            ease: 'inOutCirc',
        });
    };

    const handleBrandToggle = (brandKey) => {
        const brandGroupIndex = filters.findIndex(f => f.key === 'brand');
        if (brandGroupIndex === -1) return;

        const choiceIndex = filters[brandGroupIndex].filters[0].choices.findIndex(
            c => c.key === brandKey || c.name === brandKey
        );
        if (choiceIndex === -1) return;

        const currentActive = filters[brandGroupIndex].filters[0].choices[choiceIndex].active;
        const newFilters = dotProp.set(
            filters,
            `${brandGroupIndex}.filters.0.choices.${choiceIndex}.active`,
            !currentActive
        );

        updateFilters('filters', newFilters);
    };

    const resetFilters = event => {
        event.preventDefault();

        setQuery("");
        fetchData(1, true, new FormData());
    };

    return (
        <StaticData.Provider value={{ customOptions, placeholderUrl }}>
            <form ref={formRef}>
                <input type="hidden" name="uid" value={sessionId} />
                <input type="hidden" name="sort" value={sorting.find(sort => sort.active)?.key || customOptions?.defaultSort || ""} />
                <PortalReplace domNode={document.getElementById('product-overview-header')}>
                    <ProductOverviewHeader
                        sorting={sorting}
                        totalResults={totalResults}
                        updateSorting={(options) => updateFilters('sorting', options)}
                        filterOpen={filterOpen}
                        setFilterOpen={setFilterOpen}
                        isLoading={isLoading}
                    />
                </PortalReplace>
                <div className="container">
                    <div className="product-overview__grid">
                        <div className="product-overview__sidebar">
                            <ProductOverviewFilter
                                filters={filters}
                                updateFilters={(filters) => updateFilters('filters', filters)}
                                filterOpen={filterOpen}
                                setFilterOpen={setFilterOpen}
                                isLoading={isLoading}
                                resultsRef={resultsRef}
                                query={query}
                                updateQuery={updateQuery}
                                resetFilters={resetFilters}
                            />
                        </div>
                        <div className="product-overview__content">
                            <BrandLogoBar
                                filters={filters}
                                onBrandToggle={handleBrandToggle}
                            />

                            <div ref={resultsRef}>
                                <ProductOverviewResults
                                    isLoading={isLoading}
                                    items={items}
                                />
                            </div>
                            <ProductOverviewPagination
                                page={page - 1}
                                totalPages={totalPages}
                                updateCurrentPage={(data) => updateCurrentPage(data)}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </StaticData.Provider>
    );
}
